// Web push (FCM) opt-in for admin + family. Registers a dedicated background
// service worker, obtains an FCM token, and records it under fcmTokens/{token}
// so the server (Cloud Functions) can notify this device of new posts.
//
// This is the same token pipeline that carries over to native push when the app
// is later wrapped with Capacitor — only the token source changes.

import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import { doc, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import app, { db } from "../firebase/client";

// Public Firebase config (safe to expose) passed to the background worker via
// query string so the worker file needn't hard-code any values.
const cfg = {
  apiKey: import.meta.env.VITE_FB_API_KEY,
  authDomain: import.meta.env.VITE_FB_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FB_PROJECT_ID,
  messagingSenderId: import.meta.env.VITE_FB_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FB_APP_ID,
};
const VAPID_KEY = import.meta.env.VITE_FCM_VAPID_KEY;
const TOKEN_KEY = "fcmToken";

// Register the FCM worker on its OWN scope so it doesn't clash with the PWA
// (vite-plugin-pwa) worker that controls "/". The script lives at "/", so any
// sub-path scope is permitted without a Service-Worker-Allowed header.
async function registerFcmSW() {
  const params = new URLSearchParams(cfg).toString();
  const reg = await navigator.serviceWorker.register(
    `/firebase-messaging-sw.js?${params}`,
    { scope: "/firebase-cloud-messaging-push-scope" }
  );
  // getToken -> PushManager.subscribe needs an ACTIVE worker. A freshly
  // registered SW is still "installing"/"waiting" for a moment, which throws
  // "Subscription failed - no active Service Worker" on the first attempt. Wait
  // for it to activate before returning.
  if (!reg.active) {
    const sw = reg.installing || reg.waiting;
    if (sw) {
      await new Promise((resolve, reject) => {
        const onChange = () => {
          if (sw.state === "activated") { sw.removeEventListener("statechange", onChange); resolve(); }
          else if (sw.state === "redundant") { sw.removeEventListener("statechange", onChange); reject(new Error("Notification service worker failed to start.")); }
        };
        sw.addEventListener("statechange", onChange);
      });
    }
  }
  return reg;
}

export async function pushSupported() {
  try {
    return (
      "serviceWorker" in navigator &&
      "Notification" in window &&
      (await isSupported())
    );
  } catch {
    return false;
  }
}

export function pushEnabledLocally() {
  return (
    typeof Notification !== "undefined" &&
    Notification.permission === "granted" &&
    !!localStorage.getItem(TOKEN_KEY)
  );
}

// Ask permission, mint a token, and store it. Idempotent.
export async function enablePush({ uid, tenantId }) {
  if (!(await pushSupported())) throw new Error("Notifications aren't supported on this device or browser.");
  if (!VAPID_KEY) throw new Error("Push isn't configured yet (missing VAPID key).");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notifications were not allowed.");
  const swReg = await registerFcmSW();
  const token = await getToken(getMessaging(app), { vapidKey: VAPID_KEY, serviceWorkerRegistration: swReg });
  if (!token) throw new Error("Couldn't obtain a notification token.");
  await setDoc(
    doc(db, "fcmTokens", token),
    { uid, tenantId, platform: "web", updatedAt: serverTimestamp() },
    { merge: true }
  );
  localStorage.setItem(TOKEN_KEY, token);
  return token;
}

export async function disablePush() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    await deleteDoc(doc(db, "fcmTokens", token)).catch(() => {});
    localStorage.removeItem(TOKEN_KEY);
  }
}

// Foreground messages don't wake the background worker, so render one ourselves.
export function listenForegroundPush() {
  pushSupported().then((ok) => {
    if (!ok) return;
    onMessage(getMessaging(app), (payload) => {
      const d = payload.data || {};
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        new Notification(d.title || "Mary's Board", { body: d.body || "", icon: "/logo192.png" });
      }
    });
  });
}
